import React from "react";
import { useNavigate } from "react-router-dom";

// ==========================
// API BASE URL
// ==========================
const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
).replace(/\/$/, "");

interface AdvertisementCardProps {
  ad: any;
}

const AdvertisementCard: React.FC<AdvertisementCardProps> = ({
  ad,
}) => {
  const navigate = useNavigate();

  // ==========================
  // NO AD
  // ==========================
  if (!ad) {
    return null;
  }

  // ==========================
  // MEDIA URL
  // ==========================
  const getMediaUrl = (
    file?: string | null
  ) => {
    if (
      !file ||
      String(file).trim() === ""
    ) {
      return "";
    }

    const value =
      String(file).trim();

    // Already full URL
    if (
      value.startsWith("http://") ||
      value.startsWith("https://")
    ) {
      return encodeURI(value);
    }

    // /uploads/file.jpg
    if (
      value.startsWith("/uploads/")
    ) {
      return encodeURI(
        `${API_BASE}${value}`
      );
    }

    // uploads/file.jpg
    if (
      value.startsWith("uploads/")
    ) {
      return encodeURI(
        `${API_BASE}/${value}`
      );
    }

    // /other/path/file.jpg
    if (
      value.startsWith("/")
    ) {
      return encodeURI(
        `${API_BASE}${value}`
      );
    }

    // filename.jpg
    return encodeURI(
      `${API_BASE}/uploads/${value}`
    );
  };

  // ==========================
  // MEDIA ARRAYS
  // ==========================
  const images =
    Array.isArray(ad?.images)
      ? ad.images
      : [];

  const videos =
    Array.isArray(ad?.videos)
      ? ad.videos
      : [];

  // ==========================
  // SELECT MEDIA
  // Use first item instead of random
  // ==========================
  const selectedImage =
    images.length > 0
      ? images[0]
      : ad?.image ||
        ad?.image_url ||
        ad?.advertisement_image ||
        "";

  const selectedVideo =
    videos.length > 0
      ? videos[0]
      : ad?.video ||
        ad?.video_url ||
        ad?.advertisement_video ||
        "";

  const imageUrl =
    getMediaUrl(
      selectedImage
    );

  const videoUrl =
    getMediaUrl(
      selectedVideo
    );

  // ==========================
  // REDIRECT
  // ==========================
  const handleClick = (
    e?: React.MouseEvent
  ) => {
    if (e) {
      e.stopPropagation();
    }

    if (
      ad?.redirect_type ===
        "url" &&
      ad?.redirect_value
    ) {
      window.open(
        ad.redirect_value,
        "_blank",
        "noopener,noreferrer"
      );

      return;
    }

    if (
      ad?.redirect_type ===
        "screen" &&
      ad?.redirect_value
    ) {
      navigate(
        ad.redirect_value
      );
    }
  };

  // ==========================
  // MEDIA TYPE
  // ==========================
  const hasVideo =
    Boolean(videoUrl) &&
    (
      ad?.media_type ===
        "video" ||
      !imageUrl
    );

  return (
    <>
      <style>
        {`
          .ad-feed-card {
            width: 100%;
            background: #ffffff;
            border: 1px solid #eef2f7;
            border-radius: 22px;
            overflow: hidden;
            margin-bottom: 22px;
            box-shadow:
              0 6px 22px
              rgba(15, 23, 42, 0.05);
            transition:
              all 0.22s ease;
          }

          .ad-feed-card:hover {
            box-shadow:
              0 12px 30px
              rgba(15, 23, 42, 0.08);
          }

          .ad-feed-header {
            display: flex;
            align-items: center;
            justify-content:
              space-between;
            gap: 12px;
            padding: 15px 18px;
          }

          .ad-feed-profile {
            display: flex;
            align-items: center;
            gap: 11px;
            min-width: 0;
          }

          .ad-feed-icon {
            width: 42px;
            height: 42px;
            min-width: 42px;
            border-radius: 13px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #eff6ff;
            color: #2563eb;
            font-size: 11px;
            font-weight: 800;
            letter-spacing: 0.4px;
            border:
              1px solid
              #dbeafe;
          }

          .ad-feed-meta {
            min-width: 0;
          }

          .ad-feed-meta h4 {
            margin: 0;
            color: #111827;
            font-size: 14px;
            font-weight: 700;
            white-space: nowrap;
            overflow: hidden;
            text-overflow:
              ellipsis;
          }

          .ad-feed-meta p {
            margin: 3px 0 0;
            font-size: 11px;
            color: #94a3b8;
            font-weight: 500;
          }

          .ad-badge {
            flex-shrink: 0;
            background: #f8fafc;
            border:
              1px solid
              #e2e8f0;
            color: #64748b;
            border-radius: 999px;
            padding: 5px 9px;
            font-size: 10px;
            font-weight: 700;
          }

          .ad-feed-media-wrap {
            position: relative;
            width: 100%;
            background: #f8fafc;
            overflow: hidden;
            cursor: pointer;
          }

          .ad-feed-media {
            display: block;
            width: 100%;
            max-height: 470px;
            object-fit: cover;
          }

          video.ad-feed-media {
            background: #000;
          }

          .ad-feed-overlay {
            position: absolute;
            inset: 0;
            pointer-events: none;

            background:
              linear-gradient(
                to top,
                rgba(
                    15,
                    23,
                    42,
                    0.28
                  )
                  0%,
                rgba(
                    15,
                    23,
                    42,
                    0
                  )
                  45%
              );
          }

          .ad-feed-btn {
            position: absolute;
            right: 14px;
            bottom: 14px;
            z-index: 2;

            border: none;
            border-radius: 12px;
            padding: 9px 15px;

            background:
              rgba(
                255,
                255,
                255,
                0.96
              );

            color: #2563eb;
            font-size: 12px;
            font-weight: 700;
            cursor: pointer;

            box-shadow:
              0 8px 20px
              rgba(
                15,
                23,
                42,
                0.15
              );

            transition:
              all 0.2s ease;
          }

          .ad-feed-btn:hover {
            transform:
              translateY(-1px);
            background: #ffffff;
          }

          .ad-feed-content {
            padding: 14px 18px 17px;
          }

          .ad-feed-desc {
            margin: 0;
            color: #475569;
            font-size: 13px;
            line-height: 1.65;
          }

          .ad-feed-empty-media {
            min-height: 130px;
            display: flex;
            align-items: center;
            justify-content: center;
            background:
              linear-gradient(
                135deg,
                #eff6ff,
                #f8fafc
              );
            color: #94a3b8;
            font-size: 12px;
            font-weight: 600;
          }

          @media (
            max-width: 768px
          ) {
            .ad-feed-card {
              border-radius: 18px;
              margin-bottom: 18px;
            }

            .ad-feed-header {
              padding: 13px 14px;
            }

            .ad-feed-icon {
              width: 38px;
              height: 38px;
              min-width: 38px;
              border-radius: 11px;
            }

            .ad-feed-meta h4 {
              font-size: 13px;
            }

            .ad-feed-content {
              padding: 12px 14px 15px;
            }

            .ad-feed-media {
              max-height: 400px;
            }

            .ad-feed-btn {
              right: 10px;
              bottom: 10px;
              padding: 8px 12px;
              font-size: 11px;
            }
          }
        `}
      </style>

      <div className="ad-feed-card">
        {/* ==========================
            HEADER
        ========================== */}
        <div className="ad-feed-header">
          <div className="ad-feed-profile">
            <div className="ad-feed-icon">
              AD
            </div>

            <div className="ad-feed-meta">
              <h4>
                {ad?.title ||
                  "Sponsored Advertisement"}
              </h4>

              <p>
                Home Feed Promotion
              </p>
            </div>
          </div>

          <div className="ad-badge">
            Sponsored
          </div>
        </div>

        {/* ==========================
            MEDIA
        ========================== */}
        <div
          onClick={
            handleClick
          }
          className="ad-feed-media-wrap"
        >
          {hasVideo ? (
            <video
              src={
                videoUrl
              }
              controls
              className="ad-feed-media"
              onClick={(e) =>
                e.stopPropagation()
              }
            />
          ) : imageUrl ? (
            <img
              src={
                imageUrl
              }
              alt={
                ad?.title ||
                "Advertisement"
              }
              className="ad-feed-media"
              onError={(e) => {
                e.currentTarget.style.display =
                  "none";
              }}
            />
          ) : (
            <div className="ad-feed-empty-media">
              Advertisement
            </div>
          )}

          {(imageUrl ||
            hasVideo) && (
            <div className="ad-feed-overlay" />
          )}

          {ad?.redirect_value && (
            <button
              type="button"
              onClick={
                handleClick
              }
              className="ad-feed-btn"
            >
              {ad?.button_text ||
                ad?.cta_text ||
                "Learn More"}
            </button>
          )}
        </div>

        {/* ==========================
            CONTENT
        ========================== */}
        <div className="ad-feed-content">
          <p className="ad-feed-desc">
            {ad?.description ||
              "Explore this sponsored advertisement."}
          </p>
        </div>
      </div>
    </>
  );
};

export default AdvertisementCard;